import React, { useEffect, useState } from 'react';
import { Card, Table, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { api_url } from '../../constant/constant';
import { useTranslation } from 'react-i18next';

const PhishingCampaignUserSummary = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Empty structure for initial summary data to show empty cards/tables on page load
  const emptySummary = {
    campaign_summary: {
      name: '',
      start_date: '',
      end_date: '',
      enrolled_users_count: 0,
      phished_users_count: 0,
      awarded_users_count: 0,
    },
    users: {
      opened: { data: [], links: [] },
      clicked: { data: [], links: [] },
      submitted: { data: [], links: [] },
      phished: { data: [], links: [] },
      clear: { data: [], links: [] },
    },
  };

  const [summaryData, setSummaryData] = useState(emptySummary);
  const [error, setError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedPhishing, setSelectedPhishing] = useState('');
  const [departments, setDepartments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [openedLinks, setOpenedLinks] = useState([]);
  const [clickedLinks, setClickedLinks] = useState([]);
  const [submittedLinks, setSubmittedLinks] = useState([]);
  const [phishedLinks, setPhishedLinks] = useState([]);
  const [clearLinks, setClearLinks] = useState([]);

  // Fetch departments list
  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${api_url}/admin/main/departments`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result && result.data) {
        setDepartments(result.data);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  // Fetch phishing campaigns list
  const fetchPhishing = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${api_url}/phishing-campaigns`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result && result.data && Array.isArray(result.data.data)) {
        setCampaigns(result.data.data);
      } else {
        setCampaigns([]);
      }
    } catch (err) {
      console.error('Error fetching phishing campaigns:', err);
    }
  };

  // Fetch user summary with optional pagination URL
  const fetchUserSummary = async (customUrl = null) => {
    // Validate selected campaign
    if (!selectedPhishing) {
      setError(t('PleaseSelectCampaign'));
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('userToken');
      const url = customUrl
        ? customUrl
        : `${api_url}/report/phishing/users-summary?campaign_id=${selectedPhishing}${
            selectedDepartment ? `&department_id=${selectedDepartment}` : ''
          }&opened_per_page=5&clicked_per_page=15&submitted_per_page=5`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if ([401, 403, 429].includes(response.status)) {
        localStorage.removeItem('userToken');
        navigate('/login');
        return;
      }

      const result = await response.json();

      if (result.status === 'success') {
        setSummaryData(result.data);

        setOpenedLinks(result.data.users.opened?.links || []);
        setClickedLinks(result.data.users.clicked?.links || []);
        setSubmittedLinks(result.data.users.submitted?.links || []);
        setPhishedLinks(result.data.users.phished?.links || []);
        setClearLinks(result.data.users.clear?.links || []);

        setError(null);
      } else {
        throw new Error(result.message || 'Failed to fetch user summary');
      }
    } catch (err) {
      setError(err.message || t('AnErrorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPhishing();
    fetchDepartments();

    // Initialize pagination links as empty arrays on load
    setOpenedLinks([]);
    setClickedLinks([]);
    setSubmittedLinks([]);
    setPhishedLinks([]);
    setClearLinks([]);
  }, []);

  // Render pagination navigation for user lists
  const renderPagination = (links, onPageChange) => (
    <div
      className="navigationWrapper"
      style={{
        marginTop: '10px',
        textAlign: 'right', // aligns the whole block to the right
      }}
    >
      <ul
        style={{
          display: 'inline-flex', // make ul inline so textAlign right works on div
          listStyle: 'none',
          paddingLeft: 0,
          margin: 0,
        }}
      >
        {links.map((link, index) => (
          <li
            key={index}
            className={`${link.active ? 'active' : ''} ${link.url === null ? 'disabled' : ''}`}
            style={{
              marginRight: '8px',
              cursor: link.url !== null ? 'pointer' : 'not-allowed',
              color: link.active ? '#fff' : '#007bff',
              backgroundColor: link.active ? '#007bff' : 'transparent',
              padding: '4px 8px',
              borderRadius: '4px',
              userSelect: 'none',
            }}
            onClick={link.url !== null ? () => onPageChange(link.url) : undefined}
            dangerouslySetInnerHTML={{ __html: link.label }}
          />
        ))}
      </ul>
    </div>
  );
  
  

  // Render user tables for each category
  const renderTable = (title, users = [], links = [], onPageChange) => (
    <Card className="p-3 mb-4">
      <h5>{title}</h5>
      {users.length === 0 ? (
        <p className="text-muted">{t('NoUsersFound')}</p>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>{t('FullName')}</th>
                <th>{t('Email')}</th>
                <th>{t('Department')}</th>
                <th>{t('Opened')}</th>
                <th>{t('Clicked')}</th>
                <th>{t('Submitted')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.user_id}>
                  <td>{user.full_name}</td>
                  <td>{user.email}</td>
                  <td>{user.department}</td>
                  <td>{user.opened_count}</td>
                  <td>{user.clicked_count}</td>
                  <td>{user.submitted_count}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {renderPagination(links, onPageChange)}
        </>
      )}
    </Card>
  );

  // Pagination fetch handlers
  const fetchOpenedPage = (url) => fetchUserSummary(url);
  const fetchClickedPage = (url) => fetchUserSummary(url);
  const fetchSubmittedPage = (url) => fetchUserSummary(url);
  const fetchPhishedPage = (url) => fetchUserSummary(url);
  const fetchClearPage = (url) => fetchUserSummary(url);

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>{t('PhishingCampaignUsersSummary')}</h2>
        <Button variant="primary" onClick={() => navigate(-1)}>
          {t('Back')}
        </Button>
      </div>

      <div className="mb-4 p-3 card">
        <div
          className="p-3"
          style={{
            backgroundColor: '#f0f4f8',
            borderRadius: '16px',
          }}
        >
          <div className="row g-3 align-items-center">
            {/* Campaign Dropdown */}
            <div className="col-md-4">
              <label className="form-label">{t('SelectPhishing')}</label>
              <select
                className="form-select"
                value={selectedPhishing}
                onChange={(e) => setSelectedPhishing(e.target.value)}
              >
                <option value="">{t('SelectPhishing')}</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name_en}
                  </option>
                ))}
              </select>
            </div>

            {/* Department Dropdown */}
            <div className="col-md-4">
              <label className="form-label">{t('SelectDepartments')}</label>
              <select
                className="form-select"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value="">{t('SelectDepartment')}</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filter Button */}
            <div className="">
              <Button
                className="btn btn-success "
                onClick={() => {
                  setError(null);
                  fetchUserSummary();
                }}
              >
                {t('ApplyFilters')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isLoading && <div className="text-center py-5">{t('Loading')}...</div>}
      {error && <div className="text-danger text-center py-5">{error}</div>}

      {summaryData && (
        <>
          {/* Campaign Summary */}
          <Card className="p-3 mb-4">
            <h5>{t('CampaignSummary')}</h5>
            <p>
              <strong>{t('Name')}:</strong> {summaryData.campaign_summary.name}
            </p>
            <p>
              <strong>{t('StartDate')}:</strong> {summaryData.campaign_summary.start_date}
            </p>
            <p>
              <strong>{t('EndDate')}:</strong> {summaryData.campaign_summary.end_date}
            </p>
            <p>
              <strong>{t('EnrolledUsers')}:</strong> {summaryData.campaign_summary.enrolled_users_count}
            </p>
            <p>
              <strong>{t('PhishedUsers')}:</strong> {summaryData.campaign_summary.phished_users_count}
            </p>
            <p>
              <strong>{t('AwardedUsers')}:</strong> {summaryData.campaign_summary.awarded_users_count}
            </p>
          </Card>

          {/* User tables */}
          {renderTable(
            t('OpenedUsers'),
            summaryData.users.opened?.data || [],
            openedLinks,
            fetchOpenedPage
          )}
          {renderTable(
            t('ClickedUsers'),
            summaryData.users.clicked?.data || [],
            clickedLinks,
            fetchClickedPage
          )}
          {renderTable(
            t('SubmittedUsers'),
            summaryData.users.submitted?.data || [],
            submittedLinks,
            fetchSubmittedPage
          )}
          {renderTable(
            t('PhishedUsers'),
            summaryData.users.phished?.data || [],
            phishedLinks,
            fetchPhishedPage
          )}
          {renderTable(
            t('ClearUsers'),
            summaryData.users.clear?.data || [],
            clearLinks,
            fetchClearPage
          )}
        </>
      )}
    </div>
  );
};

export default PhishingCampaignUserSummary;
